import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
  Tabs, TabList, TabPanels, Tab, TabPanel, Box, 
  SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText, 
  useColorModeValue, Card, CardBody, Text, HStack, Badge,
  Icon, Flex, VStack, Button
} from '@chakra-ui/react';
import { FaFileInvoiceDollar, FaDollarSign, FaCheckCircle, FaClock, FaPercentage, FaCreditCard, FaSync } from 'react-icons/fa';
import { useLocation, useSearchParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { selectAll } from '../../utils/fetchAllRows';
import BatchManagement from './BatchManagement';
import InvoicesList from './InvoicesList';
import PaymentHistory from './PaymentHistory';
import OpenOrdersInvoiceBuilder from './OpenOrdersInvoiceBuilder';

function InvoicesAndBatches() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Check if batch ID is in URL params - if so, switch to Create Invoice tab
  const batchIdFromUrl = searchParams.get('batch');
  // Always default to "Create Invoice" tab (tab 0) first, unless coming from Manage Invoices
  const [activeTab, setActiveTab] = useState(batchIdFromUrl ? 0 : 0);
  const [stats, setStats] = useState({
    totalInvoices: 0,
    unpaidInvoices: 0,
    unpaidAmount: 0,
    paidInvoices: 0,
    paidAmount: 0,
    totalBatches: 0,
    pendingBatches: 0,
  });
  const [loadingStats, setLoadingStats] = useState(true);
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const statsCacheRef = useRef({ lastFetch: 0, data: null });
  const STATS_CACHE_DURATION = 3000; // Cache for 3 seconds (reduced for faster updates)

  // Switch to Create Invoice tab if batch ID is in URL
  useEffect(() => {
    if (batchIdFromUrl) {
      setActiveTab(0);
      // Clear the batch param after switching tabs
      searchParams.delete('batch');
      setSearchParams(searchParams, { replace: true });
    }
  }, [batchIdFromUrl]);

  const fetchStats = useCallback(async () => {
    // Check cache first
    const now = Date.now();
    if (statsCacheRef.current.data && (now - statsCacheRef.current.lastFetch) < STATS_CACHE_DURATION) {
      setStats(statsCacheRef.current.data);
      setLoadingStats(false);
      return;
    }

    try {
      setLoadingStats(true);
      
      // Parallelize all independent queries
      const [invoicesResult, batchesResult] = await Promise.all([
        supabase
          .from('submission_invoices')
          .select('id, payment_status, original_batch_id'),
        supabase
          .from('submission_batches')
          .select('id, status, ship_fee')
      ]);

      if (invoicesResult.error) throw invoicesResult.error;
      if (batchesResult.error) throw batchesResult.error;

      const invoices = invoicesResult.data || [];
      const batches = batchesResult.data || [];

      // Fetch submissions and phlebotomists in parallel if needed
      const batchIds = [...new Set(invoices
        .map(inv => inv.original_batch_id)
        .filter(Boolean))];
      
      let submissions = [];
      let phlebotomists = {};
      
      if (batchIds.length > 0) {
        const subsResult = await selectAll(() => supabase
          .from('submissions')
          .select('id, batch_id, phlebotomist_id, admin_fee, draw_fee_paid, invoice_price')
          .in('batch_id', batchIds));

        if (subsResult.error) throw subsResult.error;
        submissions = subsResult.data || [];

        // Fetch only phlebotomists referenced in submissions
        const phlebIds = [...new Set(submissions.map(s => s.phlebotomist_id).filter(Boolean))];
        if (phlebIds.length > 0) {
          const phlebResult = await supabase
            .from('phlebotomist_profiles')
            .select('id, lab_draw_fee')
            .in('id', phlebIds);
          
          if (phlebResult.error) throw phlebResult.error;
          const phlebData = phlebResult.data || [];
          phlebData.forEach(p => { phlebotomists[p.id] = p; });
        }
      }

      // Calculate stats
      const totalInvoices = invoices.length;
      const unpaidInvoices = invoices.filter(inv => (inv.payment_status || 'unpaid') === 'unpaid').length;
      const paidInvoices = invoices.filter(inv => inv.payment_status === 'paid').length;
      
      // Calculate unpaid amount (matching InvoicesList logic)
      const DEFAULT_ADMIN_FEE = 5;
      const effectiveAdminFee = (sub) => {
        const v = sub.admin_fee;
        if (v != null && v !== '' && !Number.isNaN(parseFloat(v)) && parseFloat(v) > 0) return parseFloat(v);
        return DEFAULT_ADMIN_FEE;
      };

      let unpaidAmount = 0;
      let paidAmount = 0;
      invoices.forEach(inv => {
        if (inv.original_batch_id) {
          const batch = batches.find(b => b.id === inv.original_batch_id);
          if (batch) {
            const batchSubs = submissions.filter(s => s.batch_id === batch.id);
            // Per-draw price = invoice_price, floored at $23 (full line total).
            const MIN_DRAW_PRICE = 23;
            let drawTotal = 0;
            batchSubs.forEach(sub => {
              const priced = parseFloat(sub.invoice_price);
              drawTotal += Math.max(MIN_DRAW_PRICE, Number.isFinite(priced) ? priced : MIN_DRAW_PRICE);
            });
            const shipFee = batch.ship_fee ? parseFloat(batch.ship_fee) : 0;
            const batchTotal = parseFloat(Number(drawTotal + shipFee).toFixed(2));
            
            if ((inv.payment_status || 'unpaid') === 'unpaid') {
              unpaidAmount += batchTotal;
            } else if (inv.payment_status === 'paid') {
              paidAmount += batchTotal;
            }
          }
        }
      });

      const totalBatches = batches.length;
      const pendingBatches = batches.filter(b => (b.status || 'pending') === 'pending').length;

      const newStats = {
        totalInvoices,
        unpaidInvoices,
        unpaidAmount,
        paidInvoices,
        paidAmount,
        totalBatches,
        pendingBatches,
      };

      // Update cache
      statsCacheRef.current = { lastFetch: now, data: newStats };
      setStats(newStats);
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Set stats to zero on error to prevent showing stale data
      setStats({
        totalInvoices: 0,
        unpaidInvoices: 0,
        unpaidAmount: 0,
        paidInvoices: 0,
        paidAmount: 0,
        totalBatches: 0,
        pendingBatches: 0,
      });
    } finally {
      setLoadingStats(false);
    }
  }, []);

  // Fetch stats on mount
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Refresh stats when switching tabs
  useEffect(() => {
    // Clear cache when tab changes to ensure fresh data
    statsCacheRef.current = { lastFetch: 0, data: null };
    fetchStats();
  }, [activeTab, fetchStats]);

  // Listen for real-time updates
  useEffect(() => {
    const channel = supabase
      .channel('invoice-stats-updates')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'submission_invoices' },
        () => {
          // Clear cache and refresh stats when invoices change
          statsCacheRef.current = { lastFetch: 0, data: null };
          fetchStats();
        }
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'billing_transactions' },
        () => {
          // Also refresh when billing transactions change
          statsCacheRef.current = { lastFetch: 0, data: null };
          fetchStats();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchStats]);

  // Memoize currency formatting
  const formatCurrency = useCallback((amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0), []);

  // Memoize payment rate calculation
  const paymentRate = useMemo(() => {
    if (loadingStats || stats.totalInvoices === 0) return '0%';
    return `${Math.round((stats.paidInvoices / stats.totalInvoices) * 100)}%`;
  }, [stats.totalInvoices, stats.paidInvoices, loadingStats]);

  return (
    <Box>

      <Tabs index={activeTab} onChange={setActiveTab} colorScheme="blue" variant="enclosed">
        <TabList px={4} borderBottom="2px solid" borderColor="gray.200">
          <Tab 
            fontWeight="semibold" 
            _selected={{ color: "blue.600", borderColor: "blue.600", borderBottom: "2px solid" }}
            _hover={{ color: "blue.500" }}
            py={4}
          >
            Batch Payments
          </Tab>
          <Tab 
            fontWeight="semibold"
            _selected={{ color: "blue.600", borderColor: "blue.600", borderBottom: "2px solid" }}
            _hover={{ color: "blue.500" }}
            py={4}
          >
            Manage Invoices
          </Tab>
          <Tab 
            fontWeight="semibold"
            _selected={{ color: "blue.600", borderColor: "blue.600", borderBottom: "2px solid" }}
            _hover={{ color: "blue.500" }}
            py={4}
          >
            Payment History
          </Tab>
        </TabList>

        <TabPanels>
          <TabPanel px={0}>
            <OpenOrdersInvoiceBuilder onInvoiceCreated={fetchStats} />
            <BatchManagement />
          </TabPanel>
          <TabPanel px={0}>
            <InvoicesList isActive={activeTab === 1} />
          </TabPanel>
          <TabPanel px={0}>
            <PaymentHistory isActive={activeTab === 2} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}

export default InvoicesAndBatches;
